import { useEffect, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useTranslation } from 'react-i18next';
import FavoriteBtn from '~/components/common/office/FavoriteBtn';

import { Button } from '~/components/common/buttons/Button';
import SVGIcon from '~/components/ui/Icons/SVGIcon';
import type { CoreOfficeDataType } from '~/types/offices';
import FullscreenGallery from './FullscreenGallery';
import mixpanelTrack from '~/hooks/useMixPanel';
import { useLanguageParam } from '~/config/i18n';
import Badge, { Variant } from '~/components/common/badges/Badge';
import OfficeAvailabilityBadge from '~/components/common/badges/OfficeAvailabilityBadge';
import TopPickBadge from '~/components/common/badges/TopPickBadge';

const ImageGallery = ({ office, mobileGallery = false }: { office: CoreOfficeDataType; mobileGallery?: boolean }) => {
  const { t } = useTranslation();
  const largePictures = office.pictures || [];
  const smallPictures = office.smallPictures || [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  // @ts-ignore
  const [swiperRef, setSwiperRef] = useState<Swiper>();

  const lang = useLanguageParam();

  const prevSlide = () => {
    swiperRef.slidePrev();
  };

  const nextSlide = () => {
    swiperRef.slideNext();
  };

  const KEY_ESCAPE = 27;
  const handleKeyDown = (e: { keyCode: number }) => {
    if (openModal) {
      if (e.keyCode === KEY_ESCAPE) {
        document.removeEventListener('keydown', handleKeyDown);
        setOpenModal(false);
      }
    }
  };

  useEffect(() => {
    if (openModal) {
      document.addEventListener('keydown', handleKeyDown);
      mixpanelTrack('E.picture_full_screen_open');
    } else {
      if (!firstLoad) {
        mixpanelTrack('E.picture_full_screen_close');
      } else {
        setFirstLoad(false);
      }
    }
  }, [openModal]);

  return (
    <div className={`image-gallery ${mobileGallery ? 'd-md-none' : 'd-none d-md-block'}`}>
      <div className={'mb-2 cursor-pointer gallery-cover-image'}>
        <Swiper
          modules={[Navigation]}
          spaceBetween={50}
          navigation={true}
          // @ts-ignore
          onInit={(swiper: Swiper) => setSwiperRef(swiper)}
          slidesPerView={1}
          onClick={() => {
            setOpenModal(true);
          }}
          onSlideChange={(e) => {
            setActiveIndex(e.activeIndex);
            setFullscreenIndex(e.activeIndex);
          }}
        >
          <div className="swiper-overlay mt-1 mt-md-3 ms-2 ms-md-3 d-flex flex-wrap gap-1">
            {office.isBestDeal && <TopPickBadge />}
            <OfficeAvailabilityBadge office={office} />
          </div>

          <FavoriteBtn
            office={office}
            extraClasses="swiper-overlay top-0 end-0 pt-1 d-none d-md-block"
          />
          {activeIndex > 0 && largePictures[activeIndex].imageType !== 'other' && (
            <Badge
              variant={Variant.PRIMARY}
              text={t(`officeSearch.card.tags.gallery.${largePictures[activeIndex].imageType}`)}
              extraClasses="bg-opacity-85 swiper-overlay bottom-0 mb-1 mb-md-3"
              ribbon={true}
            />
          )}

          <Badge
            variant={Variant.PRIMARY}
            text={`${activeIndex + 1}/${largePictures.length}`}
            extraClasses="rounded-3 bg-opacity-85 color-white swiper-overlay bottom-0 end-0 mb-1 mb-md-3 me-2 me-md-3"
          />

          {largePictures.map((imageSource, index) => (
            <SwiperSlide key={`swiper-slide-${index}`}>
              <img
                loading="lazy"
                src={imageSource.url}
                alt={imageSource.altDescription[lang]}
                height={mobileGallery ? 256 : 400}
              />
            </SwiperSlide>
          ))}
          <Button
            variant="primaryWhite"
            isIconButton={true}
            size="lg"
            floating={true}
            onClick={prevSlide}
            extraClasses={`position-absolute border-0 start-0 top-50 z-2 ms-3 d-none ${
              activeIndex > 0 ? 'd-md-block' : 'd-md-none'
            }`}
            aria-label="Previous image"
            name="Previous image"
          >
            <SVGIcon iconType="chevron-left" />
          </Button>

          <Button
            variant="primaryWhite"
            isIconButton={true}
            size="lg"
            floating={true}
            onClick={nextSlide}
            extraClasses={`position-absolute border-0 end-0 top-50 z-2 me-3 d-none ${
              activeIndex + 1 < largePictures.length ? 'd-md-block' : 'd-md-none'
            }`}
            aria-label="Next image"
            name="Next image"
          >
            <SVGIcon iconType="chevron-right" />
          </Button>
        </Swiper>
      </div>
      {!mobileGallery && (
        <div className="gallery-preview p-0 align-items-flex-start align-self-stretch d-none d-md-flex">
          {smallPictures.length > 1 && (
            <img
              className={'rounded-2 cursor-pointer gallery-image-preview'}
              src={smallPictures[1].url}
              alt={smallPictures[1].altDescription[lang]}
              onClick={() => {
                setFullscreenIndex(1);
                setOpenModal(true);
              }}
            />
          )}
          {smallPictures.length > 2 && (
            <img
              className={'rounded-2 cursor-pointer gallery-image-preview'}
              src={smallPictures[2].url}
              alt={smallPictures[2].altDescription[lang]}
              onClick={() => {
                setFullscreenIndex(2);
                setOpenModal(true);
              }}
            />
          )}
          {smallPictures.length > 3 && (
            <div
              className={'rounded-2 cursor-pointer gallery-image-preview bg-black position-relative'}
              onClick={() => {
                setFullscreenIndex(0);
                setOpenModal(true);
              }}
            >
              <img
                className={'rounded-2 w-100 h-100 d-block '}
                src={smallPictures[3].url}
                alt={smallPictures[3].altDescription[lang]}
              />
              <span
                className={
                  'gallery-preview-more-images d-block z-1 position-absolute top-50 start-50 color-white text-decoration-underline link-offset-3 text-1 -medium'
                }
              >
                {t('office.gallery.morePictures', { imageCount: smallPictures.length - 3 })}
              </span>
            </div>
          )}
        </div>
      )}
      <FullscreenGallery
        extraClass={`fullscreen-gallery ${openModal ? 'show' : 'hide'}`}
        activeIndex={fullscreenIndex}
        images={largePictures}
        setActiveIndex={setFullscreenIndex}
        closeModal={() => {
          setOpenModal(false);
          setFullscreenIndex(swiperRef.activeIndex);
        }}
        open={openModal}
      ></FullscreenGallery>
    </div>
  );
};

export default ImageGallery;
